import React, { useEffect, useState, useRef } from 'react';
import { useParams } from 'react-router';
import { useSearchParams } from 'react-router-dom';
import InfiniteScroll from 'react-infinite-scroll-component';
import tinycolor from "tinycolor2";

// API/Components
import axiosInstance from '../components/axios';
import obvmConfig from '../config';
import { dictToQueryString, formatDateTime } from '../components/enums';

import {
	ALARM_CATEGORY,
	FilterNumToEventTypes,
	AlarmEventToStatusText,
	EventTypeToColor,
} from '../components/alarm_enums';

// Material UI
import {
	Box,
	Dialog,
	DialogTitle,
	DialogContent,
	FormControlLabel,
	Grid,
	IconButton,
	Paper,
	Switch,
	Toolbar,
	Typography,
	Table,
	TableContainer,
	TableHead,
	TableBody,
	TableRow,
	TableCell,
	Tooltip,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import FilterListIcon from '@mui/icons-material/FilterList';
import ColorLensIcon from '@mui/icons-material/ColorLens';
	
const eventsPerPage = obvmConfig.EVENTS.EVENTS_PER_PAGE;

// System Trend sub-page rendering (Location Name, Title)
export default function SystemEvents() {
	const { locationNum } = useParams();

	// State Management
	const [locationInfo, setLocationInfo] = useState({
		locationName: '',
		numStrings: 0,
		numUnits: 0,
		numTemps: 0,
	});
	
	const [events, setEvents] = useState([]);
	const [moreData, setMoreData] = useState(true);
	const [apiPage, setApiPage] = useState(1); // Page number for the API call
	
	// Save filtering states and colorization
	const [filtering, setFiltering] = useState({
		system_critical:	{ enabled: true, name: 'System Critical', filter: ALARM_CATEGORY.SYSTEM_CRITICAL },
		equipment:			{ enabled: true, name: 'Equipment', filter: ALARM_CATEGORY.EQUIPMENT },
		temperature:		{ enabled: true, name: 'Temperature', filter: ALARM_CATEGORY.TEMP },
		temp_diff:			{ enabled: true, name: 'Temp. Differential', filter: ALARM_CATEGORY.TEMP_DIFF },
		// unit_volt_crit:		{ enabled: true, name: 'Unit Voltage Critical', filter: ALARM_CATEGORY.UNIT_VOLT_CRIT },
		// unit_imped_crit:	{ enabled: true, name: 'Unit Impedance Critical', filter: ALARM_CATEGORY.UNIT_IMPED_CRIT },
		// unit_volt_maint:	{ enabled: true, name: 'Unit Voltage Maintenance', filter: ALARM_CATEGORY.UNIT_VOLT_MAINT },
		// unit_imped_maint:	{ enabled: true, name: 'Unit Impedance Maintenance', filter: ALARM_CATEGORY.UNIT_IMPED_MAINT },
		unit_voltage:		{ enabled: true, name: 'Unit Voltage', filter: ALARM_CATEGORY.UNIT_VOLT_CRIT },
		unit_impedance:		{ enabled: true, name: 'Unit Impedance', filter: ALARM_CATEGORY.UNIT_IMPED_CRIT },
		discharge:			{ enabled: true, name: 'Discharge', filter: ALARM_CATEGORY.DISCHARGE },
		abort:				{ enabled: true, name: 'Abort', filter: ALARM_CATEGORY.ABORT },
		aux:				{ enabled: true, name: 'Aux Inputs', filter: ALARM_CATEGORY.AUX },
		polling:			{ enabled: true, name: 'Polling', filter: ALARM_CATEGORY.POLLING },
	});
	const [isColorized, setIsColorized] = useState(true);

	// Dialog state management
	const [dialogOpen, setDialogOpen] = useState(false);
	
	// Get information about the location id specified (name, size, num of temps)
	useEffect(() => {
		axiosInstance.get(`/loc/${locationNum}/size/`).then(response => {
			const locationInfo = response.data;
			setLocationInfo({
				locationName: locationInfo.location_name,
				serialNumber: locationInfo.serial_number,
			});
		});
	}, [locationNum]);
	
	useEffect(() => {
		getEventData(true);
	}, [locationNum, filtering]);

	// Return true if all filters are active
	function allFiltersActive() {
		for (let key in filtering) {
			if (!filtering[key].enabled) {
				return false;
			}
		}

		return true;
	}

	// Returns a list of all active filters
	function getActiveFilters() {
		let activeFilters = [];

		Object.keys(filtering).forEach((key) => {
			if (filtering[key].enabled) {
				activeFilters.push(filtering[key].filter);
			}
		});

		return activeFilters;
	}

	// Returns a list of all active filters
	function getActiveFilterAlarmTypes() {
		let activeFilters = [];

		Object.keys(filtering).forEach((key) => {
			if (filtering[key].enabled) {
				activeFilters.push(FilterNumToEventTypes(filtering[key].filter));
			}
		});

		return activeFilters;
	}

	// Get event data from the API
	function getEventData(overwrite = true) {

		// Only attempt to get new data if there is more data to get
		if (moreData) {
			// Set the events api endpoint URL
			let url = `/loc/${locationNum}/events`;

			// Filter events based on name, hierarchy, and type options
			let queryParams = {};

			if (!allFiltersActive()) {
				let activeAlarmTypes = getActiveFilterAlarmTypes();
				if (activeAlarmTypes.length > 0) {
					queryParams['type'] = activeAlarmTypes.join(',');
				}
			}

			// Add the page number to the query parameters
			if (apiPage > 1) {
				queryParams['page'] = apiPage;
			}

			// Convert the query parameters to a query string
			url +=  dictToQueryString(queryParams);

			axiosInstance.get(url).then(response => {
				if (overwrite) {
					setEvents(response.data);
				} else {
					setEvents(prevEvents => [...prevEvents, ...response.data]);
				}

				if (response.data.length >= eventsPerPage) {
					setApiPage(apiPage + 1);
					setMoreData(true);
				} else {
					setMoreData(false);
				}
			});
		}
	}

	// Update the actual filtering options when the dialog is closed
	const handleDialogClose = (dialogFiltering) => {
		setDialogOpen(false);

		setApiPage(1);
		setMoreData(true);

		setFiltering(dialogFiltering);
	};
	
	// Dialog menu for setting filtering options
	function FilterMenu({ filtering, dialogOpen, handleDialogClose }) {

		const [dialogFiltering, setDialogFiltering] = useState(filtering);
		
		// Return true if any filters are active
		function anyFiltersActive() {
			for (let key in dialogFiltering) {
				if (dialogFiltering[key].enabled) {
					return true;
				}
			}

			return false;
		}

		const handleClose = () => {
			if (anyFiltersActive()) {
				handleDialogClose(dialogFiltering);  // Only set the filtering if at least one filter is active
			} else {
				handleDialogClose(filtering);  // If no filters are active, close the dialog without saving the changes
			}
		};
		
		// Update the dialog's filtering options instead of the actual filtering options when a switch is toggled
		const handleToggle = (event) => {
			setDialogFiltering({
				...dialogFiltering,
				[event.target.name]: { ...dialogFiltering[event.target.name], enabled: event.target.checked },
			});
		};

		return (
			<Dialog open={dialogOpen} onClose={handleClose}>
				<DialogTitle>
					<Box display="flex" justifyContent="space-between" alignItems="center">
						<span>Filtering Options</span>
						<IconButton edge="end" color="inherit" onClick={handleClose} aria-label="close">
							<CloseIcon />
						</IconButton>
					</Box>
				</DialogTitle>
				<DialogContent>
					<Grid container spacing={2}>
						{Object.keys(dialogFiltering).map((key) => (
							<Grid item xs={6} key={key}>
								<FormControlLabel
									control={
										<Switch
											checked={dialogFiltering[key].enabled}
											onChange={handleToggle}
											name={key}
										/>
									}
									label={dialogFiltering[key].name}
									key={key}
								/>
							</Grid>
						))}
					</Grid>
				</DialogContent>
			</Dialog>
		);
	}

	const DateTimeStyle = {
		minWidth: '200px',
		width: '200px',
	};

	const ClearedStyle = {
		minWidth: '200px',
		'@media (max-width:1280px)': {
			display: 'none',  // Hide the column on smaller windows
		}
	};

	const EventStyle = {
		minWidth: '400px',
		width: '30%',
	};

	const NoteStyle = {
		minWidth: '200px',
		width: '20%',
	};

	const AckStyle = {
		minWidth: '200px',
		width: '200px',
		'@media (max-width:1280px)': {
			display: 'none',  // Hide the column on smaller windows
		}
	};

	const AckUserStyle = {
		minWidth: '160px',
		width: '10%',
		'@media (max-width:1280px)': {
			display: 'none',  // Hide the column on smaller windows
		}
	};

	const RetrievedStyle = {
		minWidth: '200px',
		width: '200px',
		'@media (max-width:1280px)': {
			display: 'none',  // Hide the column on smaller windows
		}
	};

	return (
		<Paper
			sx={{
				display: 'flex',
				flexDirection: 'column',
				boxShadow: '0',
				overflow: 'visible',
			}}
		>
			<Typography
				variant='h5'
				fontFamily='Saira, sans-serif'
				fontWeight='600'
				color='#0F1820'
				align='center'
				padding='5px'
			>
				{ locationInfo.locationName } (SN {locationInfo.serialNumber})
			</Typography>
			<Toolbar>
				<Box display="flex" justifyContent="space-between" alignItems="center" width='100%'>
					<Typography
						align="left"
						fontFamily='Saira,sans-serif'
						fontWeight='600'
						sx={{ flexGrow: 1}}
					>
						Events Summary
					</Typography>
					<Tooltip title="Toggle type colorization">
						<IconButton
							sx={{
								marginRight: 2,
								border: '1px solid',
								borderRadius: 1,
								color: isColorized ? 'primary.main' : 'grey.500',
							}}
							onClick={() => setIsColorized(!isColorized)}
						>
							<ColorLensIcon />
						</IconButton>
					</Tooltip>
					<IconButton
						sx={{ border: '1px solid', borderRadius: 1, borderColor: dialogOpen ? 'primary.main' : 'grey.500' }}
						onClick={() => setDialogOpen(true)}
					>
						<FilterListIcon />
						<Typography
							fontFamily='Saira,sans-serif'
							fontWeight='600'
							sx={{ marginLeft: 1 }}
						>
							Filter
						</Typography>
					</IconButton>
				</Box>
			</Toolbar>
			<FilterMenu filtering={filtering} setFiltering={setFiltering} dialogOpen={dialogOpen} handleDialogClose={handleDialogClose}/>
			<div
				id="scrollableDiv"
				style={{
					height: 'calc(100vh - 225px)',
					overflow: 'auto',
				}}
			>
				<InfiniteScroll
					dataLength={events.length}
					next={() => { getEventData(false) }}
					hasMore={moreData}
					scrollableTarget="scrollableDiv"
					loader={
						<Typography fontFamily='Saira,sans-serif' fontWeight='600' sx={{ textAlign: 'center'}}>
							Loading events...
						</Typography>
					}
					endMessage={
						<Typography fontFamily='Saira,sans-serif' fontWeight='600' sx={{ textAlign: 'center'}}>
							No more events to load.
						</Typography>
					}
				>
					<TableContainer sx={{ flex: 1, overflow: 'visible' }}>
						<Table size="small">
							<TableHead>
								<TableRow>
									<TableCell sx={{ ...DateTimeStyle, fontWeight: 'bold' }} size="small" padding="checkbox">
										Date/Time
									</TableCell>
									<TableCell sx={{ ...ClearedStyle, fontWeight: 'bold' }} size="small" padding="checkbox">
										Date/Time Cleared
									</TableCell>
									<TableCell sx={{ ...EventStyle, fontWeight: 'bold' }} size="small" padding="checkbox">
										Event
									</TableCell>
									<TableCell sx={{ ...NoteStyle, fontWeight: 'bold' }} size="small" padding="checkbox">
										Note
									</TableCell>
									<TableCell sx={{ ...AckStyle, fontWeight: 'bold' }} size="small" padding="checkbox">
										Acknowledged
									</TableCell>
									<TableCell sx={{ ...AckUserStyle, fontWeight: 'bold' }} size="small" padding="checkbox">
										Ack. User
									</TableCell>
									<TableCell sx={{ ...RetrievedStyle, fontWeight: 'bold' }} size="small" padding="checkbox">
										Retrieved
									</TableCell>
								</TableRow>
							</TableHead>
							<TableBody>
								{events.map((event, index) => (
									<TableRow
										key={index}
										sx={{
											backgroundColor: isColorized ? (
												tinycolor(EventTypeToColor(event.alarm_type, event.number)).lighten(15).toString()
											) : ( 
												index % 2 === 0 ? '#FFFFFF' : '#F2F2F2'
											),
											'&:hover': {
												backgroundColor: isColorized ? EventTypeToColor(event.alarm_type, event.number) : '#D9D9D9',
											},
										}}
									>
										<TableCell sx={DateTimeStyle} size="small" padding="checkbox">
											{formatDateTime(event.datetime)}
										</TableCell>
										<TableCell sx={ClearedStyle} size="small" padding="checkbox">
											{formatDateTime(event.clear_datetime)}
										</TableCell>
										<TableCell sx={EventStyle} size="small" padding="checkbox">
											{AlarmEventToStatusText(event)}
										</TableCell>
										<TableCell sx={NoteStyle} size="small" padding="checkbox">
											{event.note_trimmed}
										</TableCell>
										<TableCell sx={AckStyle} size="small" padding="checkbox">
											{formatDateTime(event.ack_datetime)}
										</TableCell>
										<TableCell sx={AckUserStyle} size="small" padding="checkbox">
											{event.ack_user_trimmed}
										</TableCell>
										<TableCell sx={RetrievedStyle} size="small" padding="checkbox">
											{formatDateTime(event.retrieve_datetime)}
										</TableCell>
									</TableRow>
								))}
							</TableBody>
						</Table>
					</TableContainer>
				</InfiniteScroll>
			</div>
		</Paper>
	);


}